import { Container, Heading } from "@chakra-ui/react";
import React, { FC, useMemo } from "react";
import ProductCard, { WpStockStatusEnum } from "../../molecules/ProductCard";
import CustomSwiper from "../../organisms/CustomSwiper";
import useAuth from "../../useAuth";
import { fetchAllProducts } from "../../../queries/fetchAllProducts";

type RelatedProductType = {
  featuredImage: any;
  image: {
    sourceUrl: string;
    altText: string;
  };
  name: string;
  price: string;
  regularPrice: string;
  salePrice: string;
  slug: string;
  databaseId: number;
  description: string;
  allPaActiveIngredient: { nodes: [] };
  stockStatus?: any;
  productTags: { nodes: { name: string }[] };
  tierPrice?: any;
  isOnPromotion: any;
};

interface RelatedProductsType {
  upsellData?: RelatedProductType[];
  relatedData?: RelatedProductType[];
  title?: string;
  isCart?: boolean;
}

const shuffleArray = (array: any[]) => {
  return array.sort(() => Math.random() - 0.5);
};

const RelatedProducts: FC<RelatedProductsType> = ({
  upsellData,
  relatedData,
  title,
  isCart,
}: any) => {
  const { isUnlicensedUser, loggedIn, isCustomerDefault, isBankAcc } =
    useAuth();

  const allProducts = fetchAllProducts();

  const productData = useMemo(() => {
    if (!upsellData && !relatedData) {
      return [];
    }

    let combinedProducts = [...upsellData, ...relatedData].filter(
      (item: RelatedProductType) =>
        item.stockStatus !== WpStockStatusEnum.OUT_OF_STOCK,
    );

    if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
      combinedProducts = combinedProducts.filter((item: RelatedProductType) => {
        return item.productTags?.nodes.every(
          (tag: any) =>
            tag.name !== "Unlicensed" && tag.name !== "Pharmaceuticals",
        );
      });
    }

    // Shuffle the combined products
    combinedProducts = shuffleArray(combinedProducts);

    if (combinedProducts.length < 8) {
      const numberOfProducts = 8 - combinedProducts.length;

      let randomProducts = [...allProducts];

      if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
        randomProducts = randomProducts.filter((item: RelatedProductType) => {
          return item.productTags?.nodes.every(
            (tag: any) =>
              tag.name !== "Unlicensed" && tag.name !== "Pharmaceuticals",
          );
        });
      }

      const hasDifferentSlug = (item: RelatedProductType) =>
        !combinedProducts.some((product) => product.slug === item.slug);

      randomProducts = randomProducts.filter(hasDifferentSlug);

      // Shuffle the random products before adding
      randomProducts = shuffleArray(randomProducts).slice(0, numberOfProducts);

      // If there are less than 8 upsell and related products, add random products
      combinedProducts = [...combinedProducts, ...randomProducts];
    }

    return combinedProducts.slice(0, 8);
  }, [
    upsellData,
    relatedData,
    isUnlicensedUser,
    loggedIn,
    isBankAcc,
    isCustomerDefault,
    allProducts,
  ]);

  return (
    <Container
      mx="auto"
      maxW={{ "2xl": "100%", "3xl": "8xl" }}
      py={10}
      bg="#F7FAFC"
    >
      <Heading as="h3" mb={4} fontSize="xl" fontWeight="700" lineHeight="36px">
        {title}
      </Heading>
      {productData.length > 0 && (
        <CustomSwiper
          scrollbar={false}
          slidesPerView={4}
          breakpoints={{
            320: {
              slidesPerView: 1.15,
            },
            375: {
              slidesPerView: 1.25,
            },
            500: {
              slidesPerView: 1.5,
            },
            560: {
              slidesPerView: 1.8,
            },
            800: {
              slidesPerView: 3,
            },
            1080: {
              slidesPerView: 4,
            },
          }}
          items={productData}
          itemRenderer={(item: any) => {
            const imageData =
              item.featuredImage?.node?.localFile?.childImageSharp
                ?.gatsbyImageData;
            const altText = item.featuredImage?.node?.altText;
            return (
              <ProductCard
                salePrice={item.salePrice}
                heading="h3"
                name={item?.name}
                imageData={isCart ? item.image?.sourceUrl : imageData}
                altText={isCart ? item.image?.altText : altText}
                activeSubstance={item?.allPaActiveIngredient?.nodes}
                regularPrice={item?.regularPrice}
                slug={item?.slug}
                productId={item.databaseId}
                isRelatedProducts={false}
                stockStatus={item?.stockStatus}
                tierPrice={item?.tierPrice?.tierPrice}
                isProductOnPromotion={item?.isOnPromotion?.isonpromotion}
                isCart
                imageUrl={item.image?.sourceUrl}
              />
            );
          }}
        />
      )}
    </Container>
  );
};

export default RelatedProducts;
